//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Grid, InputAdornment, InputLabel, Paper, TextField, Typography } from '@mui/material'
import {
  commaDecStringToBigNumberWei,
  commaDecStringToErc20Wei,
  commaDecStringToErcDecString,
  erc20WeiToCommaDecString,
  erc20WeiToErc20DecString,
  toCommaDecString,
} from '@shared/conversions'
import { PoolAccountCTO, PoolOverviewCTO } from '@shared/sharedTypes'
import React, { ReactElement } from 'react'
import ButtonAction from '@react/components/elements/ButtonAction'

import { useOtDispatch } from '@react/redux/store'
import { redeemFromPoolTx } from '@react/signedTxs/redeemFromPoolTx'
import { logOT } from '@shared/loggingShared'
import { textFieldValueToBigNumberWei } from '@react/utils/textFieldsUtils'

export default function LenderWithdrawForm(props: {
  poolOverviewCTO: PoolOverviewCTO
  poolAccountCTO: PoolAccountCTO
}): ReactElement {
  const { poolOverviewCTO, poolAccountCTO } = props

  const dispatch = useOtDispatch()
  const exchangeRate = Number(poolOverviewCTO?.activeLoanState.exchangeRateAtMaturity ?? 1)
  const liquidityTokenSymbol = poolOverviewCTO.liquidityTokenSymbol

  const [valuePPT, setValuePPT] = React.useState<string>(erc20WeiToErc20DecString(poolAccountCTO?.maxRedeem))
  const [errorUSDC, setErrorUSDC] = React.useState<string | undefined>(undefined)
  const [errorPPT, setErrorPPT] = React.useState<string | undefined>(undefined)

  const handleCancel = (): void => {
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }
  const handleWithdraw = (): void => {
    dispatch(
      redeemFromPoolTx({
        poolAddr: poolOverviewCTO.poolAddr,
        sharesInWei: commaDecStringToErc20Wei(valuePPT),
      })
    )
    // }
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }

  const errorCheck = (usdc: bigint, ppt: bigint): void => {
    logOT.debug('REDEEM SHARES', ppt, usdc)

    if (usdc > BigInt(poolAccountCTO?.maxWithdrawRequest)) {
      setErrorUSDC(
        `Amount exceeds max withdraw  ${erc20WeiToCommaDecString(poolAccountCTO?.maxWithdraw)} ${liquidityTokenSymbol}`
      )
    } else {
      setErrorUSDC(undefined)
    }
    if (ppt > BigInt(poolAccountCTO?.maxWithdrawRequest)) {
      setErrorPPT(
        `Amount exceeds max redemption  ${erc20WeiToCommaDecString(poolAccountCTO?.maxRedeem)} ${
          poolOverviewCTO.symbol
        }`
      )
    } else {
      setErrorPPT(undefined)
    }
  }

  const handleOnFocusPPT = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    logOT.debug('handleOnFocusPPT1', valuePPT)
    const ppt = commaDecStringToBigNumberWei(valuePPT)
    setValuePPT(commaDecStringToErcDecString(valuePPT))

    const usdc = ppt * BigInt(exchangeRate)
    errorCheck(usdc, ppt)
  }
  const handleOnBlurPPT = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    logOT.debug('handleOnBlur1', valuePPT)
    setValuePPT(toCommaDecString(valuePPT))
  }
  const handleOnChangePPT = (event: React.ChangeEvent<HTMLInputElement>): void => {
    logOT.debug('handleOnChange', event.target.value)

    setValuePPT(event.target.value)
    const ppt = textFieldValueToBigNumberWei(event)
    const usdc = ppt * BigInt(exchangeRate)
    errorCheck(usdc, ppt)
  }
  return (
    <Paper>
      <Box px={3} py={2}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Typography variant="bodyBold" align="center" margin="dense">
              {` Withdraw - ${erc20WeiToCommaDecString(
                poolAccountCTO?.maxWithdraw
              )} ${liquidityTokenSymbol} available to withdraw`}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="bodyBold" align="center" margin="dense">
              {`Redeem  - ${erc20WeiToCommaDecString(poolAccountCTO?.maxRedeem)} ${
                poolOverviewCTO.symbol
              } available to Redeem`}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <InputLabel htmlFor="ppt">Withdraw ({poolOverviewCTO.symbol} Tokens)</InputLabel>
            <TextField
              id="ppt"
              InputProps={{
                endAdornment: <InputAdornment position="start">{poolOverviewCTO.symbol}</InputAdornment>,
              }}
              sx={{ width: '300px' }}
              variant="outlined"
              placeholder="Enter Withdraw Amount"
              value={valuePPT}
              type={'tel'}
              onFocus={handleOnFocusPPT}
              onChange={handleOnChangePPT}
              onBlur={handleOnBlurPPT}
              error={!!errorPPT}
              helperText={errorPPT}
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            <ButtonAction
              disabled={!!errorUSDC}
              sx={{ marginRight: '16px' }}
              variant={'primary'}
              onClick={handleWithdraw}
            >
              Withdraw
            </ButtonAction>
            <ButtonAction variant={'tertiary'} onClick={handleCancel}>
              Cancel
            </ButtonAction>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  )
}
